/**
 * Admin → Analytics Dashboard
 * ──────────────────────────────────────────────────────────────────────────
 * Route: /admin/analytics
 * Roles: super_admin, admin, marketing
 *
 * Sections:
 *  1. KPI cards (sessions / uniques / page views / avg duration / bounce / live)
 *  2. Live traffic feed (5 s polling)
 *  3. 90-day traffic line chart
 *  4. Top pages bar chart
 *  5. Device distribution pie
 *  6. Country distribution table
 *  7. Event performance table
 *  8. CSV export button
 *
 * Network shape uses the project's `api` axios instance (admin JWT bearer).
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import AdminShell from '../../components/admin/AdminShell';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Activity, Users, Eye, Timer, MousePointerClick, Globe,
  Download, RefreshCw,
} from 'lucide-react';

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BASE_URL });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem('su_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const COLORS = ['#D4A843', '#F1C870', '#88B388', '#7494C3', '#C77E5C', '#8E6FA8', '#777777'];

function KpiCard({ icon: Icon, label, value, sub, testId }) {
  return (
    <Card className="bg-[#101013] border-[#22221F] p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#9A9A92]">{label}</span>
        <span className="w-8 h-8 rounded-full flex items-center justify-center bg-[#1a1814] border border-[rgba(212,168,67,0.25)]">
          <Icon size={14} className="text-[#D4A843]" />
        </span>
      </div>
      <div className="text-3xl font-semibold tabular-nums text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-[#7A7A72]">{sub}</div>}
    </Card>
  );
}

function fmtSec(seconds) {
  const s = Math.round(seconds || 0);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}m ${r}s`;
}
function fmtPct(p) {
  return `${(Math.round((p || 0) * 1000) / 10).toFixed(1)}%`;
}

export default function AnalyticsPage() {
  const [days, setDays] = useState(90);
  const [live, setLive] = useState(null);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loadingLive, setLoadingLive] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const fetchLive = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/analytics/live');
      setLive(r.data);
    } catch (_e) {
      // silent — keep prior data
    } finally {
      setLoadingLive(false);
    }
  }, []);

  const fetchSummary = useCallback(async (d) => {
    setLoadingSummary(true);
    try {
      const [s, ev] = await Promise.all([
        api.get(`/api/admin/analytics/summary?days=${d}`),
        api.get(`/api/admin/analytics/events?days=${d}`),
      ]);
      setSummary(s.data);
      setEvents(ev.data?.events || []);
    } catch (_e) {
      // silent
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  // Initial + window-change load
  useEffect(() => {
    fetchSummary(days);
  }, [days, fetchSummary, refreshTick]);

  // Live polling — every 5s
  useEffect(() => {
    fetchLive();
    const id = setInterval(fetchLive, 5000);
    return () => clearInterval(id);
  }, [fetchLive, refreshTick]);

  // Derived chart data
  const deviceChart = useMemo(
    () => (summary?.devices || []).map(d => ({ name: d.device || 'unknown', value: d.sessions })),
    [summary]
  );
  const topPagesChart = useMemo(
    () => (summary?.top_pages || []).slice(0, 10).map(p => ({
      path: p.path.length > 28 ? p.path.slice(0, 27) + '…' : p.path,
      views: p.views,
    })),
    [summary]
  );

  const downloadCsv = () => {
    const token = localStorage.getItem('su_token') || localStorage.getItem('admin_token') || localStorage.getItem('token');
    const url = `${BASE_URL}/api/admin/analytics/export.csv?days=${days}`;
    // Use fetch + Blob so we can attach the bearer header (anchor download wouldn't)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const dl = document.createElement('a');
        const u = URL.createObjectURL(blob);
        dl.href = u;
        dl.download = `scratch-analytics-${days}d.csv`;
        document.body.appendChild(dl); dl.click(); dl.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => { /* silent */ });
  };

  return (
    <AdminShell title="Analytics">
      <div className="space-y-6">

        {/* ── Toolbar ────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#7A7A72]">Window</span>
            {[7, 30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`analytics-window-${d}`}
                className={`h-8 px-3 rounded-md text-xs font-mono border transition-colors duration-150 ${
                  days === d
                    ? 'bg-[#D4A843] text-[#120F08] border-[#D4A843]'
                    : 'border-[#2A2A2A] text-[#B9B9B9] hover:bg-[#1A1A1A]'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="analytics-refresh"
              onClick={() => setRefreshTick(t => t + 1)}
              className="border-[#2A2A2A] text-[#B9B9B9] hover:bg-[#1A1A1A]"
            >
              <RefreshCw size={14} className="mr-1.5" /> Refresh
            </Button>
            <Button
              size="sm"
              data-testid="analytics-export-csv"
              onClick={downloadCsv}
              className="bg-[#D4A843] hover:bg-[#E0B450] text-[#120F08]"
            >
              <Download size={14} className="mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* ── KPI cards ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {loadingSummary ? (
            [1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-28 bg-[#1A1A1A] rounded-md" />)
          ) : (
            <>
              <KpiCard icon={Activity} label="Live now" value={live?.total ?? '—'}
                       sub={`${live?.live_window_seconds ?? 30}s window`} testId="kpi-live" />
              <KpiCard icon={Users} label="Sessions" value={(summary?.total_sessions || 0).toLocaleString()}
                       sub={`Last ${days} days`} testId="kpi-sessions" />
              <KpiCard icon={MousePointerClick} label="Unique visitors"
                       value={(summary?.unique_visitors || 0).toLocaleString()}
                       sub={`By hashed IP`} testId="kpi-uniques" />
              <KpiCard icon={Eye} label="Page views" value={(summary?.page_views || 0).toLocaleString()}
                       sub={`All paths`} testId="kpi-views" />
              <KpiCard icon={Timer} label="Avg duration" value={fmtSec(summary?.avg_duration_sec)}
                       sub={`Per session`} testId="kpi-duration" />
              <KpiCard icon={Globe} label="Bounce rate" value={fmtPct(summary?.bounce_rate)}
                       sub={`≤1 view, <10s`} testId="kpi-bounce" />
            </>
          )}
        </div>

        {/* ── Live feed ──────────────────────────────────────────── */}
        <Card className="bg-[#101013] border-[#22221F] p-5" data-testid="live-feed-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2FBF71] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2FBF71]"></span>
              </span>
              <h3 className="text-sm font-semibold text-white">Live traffic feed</h3>
              <span className="text-xs text-[#7A7A72]">— refreshes every 5s</span>
            </div>
            <span className="text-xs text-[#7A7A72]" data-testid="live-feed-count">
              {live?.total || 0} active
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22221F] text-[10px] uppercase tracking-wider text-[#6F6F6F]">
                  <th className="py-2 pr-3 text-left">Session</th>
                  <th className="py-2 pr-3 text-left">Path</th>
                  <th className="py-2 pr-3 text-left">Device</th>
                  <th className="py-2 pr-3 text-left">Country / City</th>
                  <th className="py-2 pr-3 text-left">Promoter</th>
                  <th className="py-2 pr-3 text-right">Views</th>
                  <th className="py-2 pr-3 text-right">Duration</th>
                </tr>
              </thead>
              <tbody>
                {loadingLive && [1, 2, 3, 4].map(i => (
                  <tr key={i}><td colSpan={7}><Skeleton className="h-6 bg-[#181818] my-1" /></td></tr>
                ))}
                {!loadingLive && (live?.feed || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-[#6F6F6F]">No active visitors right now.</td></tr>
                )}
                {!loadingLive && (live?.feed || []).map(r => (
                  <tr key={r.session_id} className="border-b border-[#1c1c1c]" data-testid={`live-row-${r.session_id_short}`}>
                    <td className="py-2 pr-3 font-mono text-xs text-[#D4A843]">{r.session_id_short}</td>
                    <td className="py-2 pr-3 text-[#F2F2F2] truncate max-w-[260px]">{r.current_path}</td>
                    <td className="py-2 pr-3 text-[#B9B9B9] capitalize">{r.device_type || '—'}</td>
                    <td className="py-2 pr-3 text-[#B9B9B9]">
                      {(r.country || 'Unknown')}
                      {r.city && r.city !== 'Unknown' && r.city !== 'Local' ? <span className="text-[#7A7A72]"> · {r.city}</span> : null}
                    </td>
                    <td className="py-2 pr-3 font-mono text-[#D4A843] text-xs">{r.promoter_code || '—'}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{r.page_view_count}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{fmtSec(r.total_duration_seconds)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* ── Daily traffic line chart ───────────────────────────── */}
        <Card className="bg-[#101013] border-[#22221F] p-5" data-testid="daily-chart-card">
          <h3 className="text-sm font-semibold text-white mb-3">Daily traffic — last {days} days</h3>
          {loadingSummary ? <Skeleton className="h-64 bg-[#181818]" /> : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={summary?.daily || []} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#22221F" />
                <XAxis dataKey="date" stroke="#7A7A72" fontSize={10} />
                <YAxis stroke="#7A7A72" fontSize={10} />
                <Tooltip contentStyle={{ background: '#101013', border: '1px solid #22221F', color: '#fff', borderRadius: 6 }} />
                <Legend />
                <Line type="monotone" dataKey="sessions" stroke="#D4A843" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="uniques"  stroke="#88B388" dot={false} strokeWidth={2} />
                <Line type="monotone" dataKey="views"    stroke="#7494C3" dot={false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* ── Top pages + Device pie side-by-side ───────────────── */}
        <div className="grid lg:grid-cols-2 gap-5">
          <Card className="bg-[#101013] border-[#22221F] p-5" data-testid="top-pages-card">
            <h3 className="text-sm font-semibold text-white mb-3">Top pages</h3>
            {loadingSummary ? <Skeleton className="h-56 bg-[#181818]" /> : topPagesChart.length === 0 ? (
              <div className="text-center text-[#6F6F6F] py-10 text-sm">No data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={topPagesChart} layout="vertical" margin={{ top: 4, right: 16, left: 16, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#22221F" />
                  <XAxis type="number" stroke="#7A7A72" fontSize={10} />
                  <YAxis type="category" dataKey="path" stroke="#B9B9B9" fontSize={10} width={170} />
                  <Tooltip contentStyle={{ background: '#101013', border: '1px solid #22221F', color: '#fff', borderRadius: 6 }} />
                  <Bar dataKey="views" fill="#D4A843" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>

          <Card className="bg-[#101013] border-[#22221F] p-5" data-testid="device-pie-card">
            <h3 className="text-sm font-semibold text-white mb-3">Device distribution</h3>
            {loadingSummary ? <Skeleton className="h-56 bg-[#181818]" /> : deviceChart.length === 0 ? (
              <div className="text-center text-[#6F6F6F] py-10 text-sm">No data yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={deviceChart} dataKey="value" nameKey="name"
                    innerRadius={60} outerRadius={100} paddingAngle={2}
                  >
                    {deviceChart.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend />
                  <Tooltip contentStyle={{ background: '#101013', border: '1px solid #22221F', color: '#fff', borderRadius: 6 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Card>
        </div>

        {/* ── Countries + Event performance ─────────────────────── */}
        <div className="grid lg:grid-cols-2 gap-5">
          <Card className="bg-[#101013] border-[#22221F] p-5" data-testid="country-table-card">
            <h3 className="text-sm font-semibold text-white mb-3">Country distribution</h3>
            <div className="overflow-y-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-[#6F6F6F] border-b border-[#22221F]">
                  <tr><th className="py-2 pr-3 text-left">Country</th><th className="py-2 pr-3 text-right">Sessions</th></tr>
                </thead>
                <tbody>
                  {(summary?.countries || []).map(c => (
                    <tr key={c.country} className="border-b border-[#1c1c1c]">
                      <td className="py-2 pr-3 text-[#F2F2F2]">{c.country}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{c.sessions}</td>
                    </tr>
                  ))}
                  {(summary?.countries || []).length === 0 && (
                    <tr><td colSpan={2} className="text-center text-[#6F6F6F] py-6">No data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="bg-[#101013] border-[#22221F] p-5" data-testid="events-table-card">
            <h3 className="text-sm font-semibold text-white mb-3">Event performance</h3>
            <div className="overflow-y-auto max-h-80">
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-[#6F6F6F] border-b border-[#22221F]">
                  <tr>
                    <th className="py-2 pr-3 text-left">Path</th>
                    <th className="py-2 pr-3 text-right">Uniq</th>
                    <th className="py-2 pr-3 text-right">Views</th>
                    <th className="py-2 pr-3 text-right">Avg dur</th>
                    <th className="py-2 pr-3 text-right">Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {(events || []).map(e => (
                    <tr key={e.path} className="border-b border-[#1c1c1c]">
                      <td className="py-2 pr-3 text-[#F2F2F2] truncate max-w-[180px]">{e.path}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{e.unique_visitors}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{e.total_views}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{fmtSec(e.avg_duration_sec)}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-[#B9B9B9]">{fmtPct(e.bounce_rate)}</td>
                    </tr>
                  ))}
                  {(events || []).length === 0 && (
                    <tr><td colSpan={5} className="text-center text-[#6F6F6F] py-6">No event page traffic yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

      </div>
    </AdminShell>
  );
}
